import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Menu, PlusCircle, Mail, Info, Loader2, ChevronRight, X, User as UserIcon, LogIn, LogOut, Shield, FileText, Megaphone, LayoutDashboard, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "./Logo";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { submitEvent } from "../lib/events";
import { getIsAdmin } from "../lib/bannerApi";
import { useAuth } from "../context/AuthContext";
import { signOut } from "../lib/authApi";

const CATEGORIES = [
  "Cars",
  "Motorsport",
  "Motorcycles",
  "Shows",
  "Meets & Coffee",
  "Track Days",
];

const EMPTY = {
  name: "",
  category: "",
  date: "",
  time: "",
  end_date: "",
  end_time: "",
  location: "",
  description: "",
  website: "",
  ticket_link: "",
  social_link: "",
  organiser_email: "",
};

const fieldClass =
  "border-white/10 bg-[#121212] text-white placeholder:text-white/30 focus-visible:ring-[#ff2a00]";

// Set this to an external HTTPS shop URL to turn the "Shop" menu item into a link.
const SHOP_URL = "";

export const AppMenu = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [dialog, setDialog] = useState(null); // 'submit' | 'contact' | 'about'
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user) getIsAdmin().then(setIsAdmin);
    else setIsAdmin(false);
  }, [user]);

  const open = (d) => {
    setSheetOpen(false);
    setDialog(d);
  };

  const go = (path) => {
    setSheetOpen(false);
    navigate(path);
  };

  const handleLogout = async () => {
    await signOut();
    setSheetOpen(false);
    toast.success("Logged out");
    navigate("/");
  };

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category || !form.date || !form.location || !form.description) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEvent(form);
      toast.success("Thanks! Your event was submitted for review.");
      setForm(EMPTY);
      setDialog(null);
    } catch {
      toast.error("Could not submit right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const items = [
    { key: "contact", label: "Contact Us", icon: Mail, testid: "menu-contact" },
    { key: "about", label: "About RoadRnr", icon: Info, testid: "menu-about" },
  ];

  return (
    <>
      <button
        data-testid="menu-btn"
        aria-label="Open menu"
        onClick={() => setSheetOpen(true)}
        className="flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-[#0d0d0d] shadow-lg shadow-black/40 ring-1 ring-white/5 transition-opacity duration-200 active:opacity-70"
      >
        <Menu className="h-6 w-6 text-white" strokeWidth={2.4} />
      </button>

      <MobileDrawer open={sheetOpen} onClose={() => setSheetOpen(false)}>
        <button
          data-testid="menu-close"
          aria-label="Close menu"
          onClick={() => setSheetOpen(false)}
          className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full text-white/70 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="flex flex-col space-y-1 pr-10">
          <Logo className="h-auto w-[130px] object-contain" />
          <p className="text-sm text-white/40">Menu</p>
        </div>

        {/* Account */}
        <div className="mt-6">
          {user ? (
            <button
              data-testid="menu-profile"
              onClick={() => go("/profile")}
              className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-[#121212] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#1a1a1a]"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff2a00]/15">
                <UserIcon className="h-5 w-5 text-[#ff2a00]" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-semibold text-white">
                  Your profile
                </span>
                <span className="block truncate text-xs text-white/40">
                  {user.email}
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-white/30" />
            </button>
          ) : (
            <button
              data-testid="menu-login"
              onClick={() => go("/login")}
              className="flex w-full items-center gap-3 rounded-2xl border border-[#ff2a00]/40 bg-[#ff2a00]/10 px-4 py-4 text-left transition-colors duration-200 hover:bg-[#ff2a00]/15"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff2a00]/20">
                <LogIn className="h-5 w-5 text-[#ff2a00]" />
              </span>
              <span className="flex-1">
                <span className="block text-[15px] font-semibold text-white">
                  Log in / Sign up
                </span>
                <span className="block text-xs text-white/40">
                  Save events and list your own
                </span>
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-white/30" />
            </button>
          )}
        </div>

        <div className="mt-4 flex flex-col gap-2">
          <button
            data-testid="menu-advertise"
            onClick={() => go("/advertise")}
            className="flex items-center gap-3 rounded-2xl border border-white/5 bg-[#121212] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#1a1a1a]"
          >
            <Megaphone className="h-5 w-5 text-[#ff2a00]" />
            <span className="flex-1 text-[15px] font-semibold">Advertise on RoadRnr</span>
            <ChevronRight className="h-4 w-4 text-white/30" />
          </button>
          {SHOP_URL ? (
            <a
              href={SHOP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="menu-shop"
              onClick={() => setSheetOpen(false)}
              className="flex items-center gap-3 rounded-2xl border border-white/5 bg-[#121212] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#1a1a1a]"
            >
              <ShoppingBag className="h-5 w-5 text-[#ff2a00]" />
              <span className="flex-1 text-[15px] font-semibold">Shop</span>
              <ChevronRight className="h-4 w-4 text-white/30" />
            </a>
          ) : (
            <div
              data-testid="menu-shop"
              className="flex items-center gap-3 rounded-2xl border border-white/5 bg-[#121212] px-4 py-4 text-left"
            >
              <ShoppingBag className="h-5 w-5 text-[#ff2a00]" />
              <span className="flex-1 text-[15px] font-semibold">Shop</span>
              <span className="rounded-full bg-[#ff2a00] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                Coming soon
              </span>
            </div>
          )}
        </div>

        <div className="mt-2 flex flex-col gap-2">
          {items.map(({ key, label, icon: Icon, testid }) => (
            <button
              key={key}
              data-testid={testid}
              onClick={() => open(key)}
              className="flex items-center gap-3 rounded-2xl border border-white/5 bg-[#121212] px-4 py-4 text-left transition-colors duration-200 hover:bg-[#1a1a1a]"
            >
              <Icon className="h-5 w-5 text-[#ff2a00]" />
              <span className="flex-1 text-[15px] font-semibold">{label}</span>
              <ChevronRight className="h-4 w-4 text-white/30" />
            </button>
          ))}
        </div>

        {user && (
          <button
            data-testid="menu-logout"
            onClick={handleLogout}
            className="mt-2 flex items-center gap-3 rounded-2xl border border-white/5 px-4 py-4 text-left text-white/60 transition-colors duration-200 hover:bg-[#1a1a1a] hover:text-white"
          >
            <LogOut className="h-5 w-5" />
            <span className="flex-1 text-[15px] font-semibold">Log out</span>
          </button>
        )}

        <div className="mt-4 flex flex-col gap-1 border-t border-white/10 pt-4 pb-16">
          {isAdmin && (
            <button
              data-testid="menu-admin"
              onClick={() => go("/admin")}
              className="flex items-center gap-3 rounded-xl px-2 py-2 text-left text-sm text-white/50 transition-colors hover:text-white"
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="flex-1">Admin</span>
            </button>
          )}
          <button
            data-testid="menu-privacy"
            onClick={() => go("/privacy")}
            className="flex items-center gap-3 rounded-xl px-2 py-2 text-left text-sm text-white/50 transition-colors hover:text-white"
          >
            <Shield className="h-4 w-4" />
            <span className="flex-1">Privacy Policy</span>
          </button>
          <button
            data-testid="menu-terms"
            onClick={() => go("/terms")}
            className="flex items-center gap-3 rounded-xl px-2 py-2 text-left text-sm text-white/50 transition-colors hover:text-white"
          >
            <FileText className="h-4 w-4" />
            <span className="flex-1">Terms of Service</span>
          </button>
        </div>

        <p className="absolute bottom-6 left-6 text-xs text-white/30">
          Community-powered automotive events
        </p>
      </MobileDrawer>

      {/* Submit an Event */}
      <Dialog open={dialog === "submit"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent
          data-testid="submit-dialog"
          className="max-h-[88vh] overflow-y-auto border-white/10 bg-[#0a0a0a] text-white sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold text-white">
              Submit an Event
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Share an automotive event with the community. Submissions are
              reviewed before appearing on RoadRnr.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="mt-2 space-y-4">
            <Field label="Event name" required>
              <Input
                data-testid="submit-name"
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. Cotswolds Classic Meet"
                className={fieldClass}
              />
            </Field>

            <Field label="Category" required>
              <Select value={form.category} onValueChange={set("category")}>
                <SelectTrigger
                  data-testid="submit-category"
                  className="border-white/10 bg-[#121212] text-white"
                >
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-[#1e1e1e] text-white">
                  {CATEGORIES.map((c) => (
                    <SelectItem key={c} value={c} className="focus:bg-white/10 focus:text-white">
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Start date" required>
                <Input
                  data-testid="submit-date"
                  type="date"
                  value={form.date}
                  onChange={set("date")}
                  className={fieldClass}
                />
              </Field>
              <Field label="Start time">
                <Input
                  data-testid="submit-time"
                  type="time"
                  value={form.time}
                  onChange={set("time")}
                  className={fieldClass}
                />
              </Field>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="End date (optional)">
                <Input
                  data-testid="submit-end-date"
                  type="date"
                  min={form.date || undefined}
                  value={form.end_date}
                  onChange={set("end_date")}
                  className={fieldClass}
                />
              </Field>
              <Field label="End time (optional)">
                <Input
                  data-testid="submit-end-time"
                  type="time"
                  value={form.end_time}
                  onChange={set("end_time")}
                  className={fieldClass}
                />
              </Field>
            </div>

            <Field label="Location" required>
              <Input
                data-testid="submit-location"
                value={form.location}
                onChange={set("location")}
                placeholder="Venue, city"
                className={fieldClass}
              />
            </Field>

            <Field label="Description" required>
              <Textarea
                data-testid="submit-description"
                value={form.description}
                onChange={set("description")}
                placeholder="Tell us about the event..."
                rows={3}
                className={fieldClass}
              />
            </Field>

            <Field label="Official website link">
              <Input
                data-testid="submit-website"
                value={form.website}
                onChange={set("website")}
                placeholder="https://"
                className={fieldClass}
              />
            </Field>

            <Field label="Ticket / registration link (optional)">
              <Input
                data-testid="submit-ticket"
                value={form.ticket_link}
                onChange={set("ticket_link")}
                placeholder="https://"
                className={fieldClass}
              />
            </Field>

            <Field label="Social media link (optional)">
              <Input
                data-testid="submit-social"
                value={form.social_link}
                onChange={set("social_link")}
                placeholder="https://instagram.com/..."
                className={fieldClass}
              />
            </Field>

            <Field label="Organiser contact email (optional)">
              <Input
                data-testid="submit-email"
                type="email"
                value={form.organiser_email}
                onChange={set("organiser_email")}
                placeholder="you@example.com"
                className={fieldClass}
              />
            </Field>

            <button
              type="submit"
              data-testid="submit-event-btn"
              disabled={submitting}
              className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                "Submit for review"
              )}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Us */}
      <Dialog open={dialog === "contact"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent
          data-testid="contact-dialog"
          className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold text-white">
              Contact Us
            </DialogTitle>
            <DialogDescription className="text-white/50">
              Questions, partnerships or feedback? We'd love to hear from you.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 space-y-3">
            <a
              href="mailto:roadrnrapp@gmail.com"
              className="flex items-center gap-3 rounded-2xl border border-white/5 bg-[#121212] px-4 py-4"
            >
              <Mail className="h-5 w-5 text-[#ff2a00]" />
              <span className="text-[15px] font-semibold">roadrnrapp@gmail.com</span>
            </a>
            <p className="text-sm leading-relaxed text-white/50">
              For event listing issues, include the event name and location and
              we'll get back to you within a couple of days.
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* About RoadRnr */}
      <Dialog open={dialog === "about"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent
          data-testid="about-dialog"
          className="border-white/10 bg-[#0a0a0a] text-white sm:max-w-md"
        >
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-extrabold text-white">
              About RoadRnr
            </DialogTitle>
            <DialogDescription className="text-white/50">
              A curated automotive event discovery platform.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-1 space-y-3 text-sm leading-relaxed text-white/60">
            <p>
              RoadRnr is a curated discovery platform for automotive events —
              car meets, track days, shows, drives and everything in between.
            </p>
            <p>
              Explore what's happening around you on the map, save the events you
              love, and get directions in a tap. Every event is reviewed to keep
              the listings accurate and worth your time.
            </p>
            <p className="text-white/30">Built for enthusiasts, by enthusiasts.</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Field = ({ label, required, children }) => (
  <div className="space-y-1.5">
    <Label className="text-xs font-semibold uppercase tracking-wide text-white/50">
      {label} {required && <span className="text-[#ff2a00]">*</span>}
    </Label>
    {children}
  </div>
);

// Self-contained mobile side drawer, portaled to <body> so it sits at the app's
// top stacking context (above the map, cards and floating controls). It self-centers
// via mx-auto max-w-md, keeping it aligned to the phone-frame column. Body scroll is
// locked while open, and the panel is clipped so it never spills outside the screen.
const MobileDrawer = ({ open, onClose, children }) => {
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] mx-auto max-w-md overflow-hidden md:max-w-none ${
        open ? "pointer-events-auto" : "pointer-events-none"
      }`}
      aria-hidden={!open}
    >
      <div
        data-testid="menu-overlay"
        onClick={onClose}
        className={`absolute inset-0 bg-black/70 transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />
      <div
        data-testid="menu-drawer"
        className={`absolute right-0 top-0 flex h-full w-4/5 md:w-[400px] flex-col overflow-y-auto overflow-x-hidden border-l border-white/10 bg-[#0a0a0a] p-6 text-white shadow-2xl transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};
